import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

/**
 * TimelineMilestoneCard - Milestone card on the timeline that navigates to detail view
 */
function TimelineMilestoneCard({ milestone, onView }) {
  const navigate = useNavigate();

  const {
    id,
    title,
    year,
    month,
    location,
    emoji,
    voice_url,
    voice_duration,
    text,
    photo_urls,
    reaction_counts,
    questions,
    life_event_id,
  } = milestone;

  // Format month
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthStr = month ? months[month - 1] : null;

  // Format date string
  const dateStr = monthStr ? `${monthStr} ${year}` : year;

  // Photo count
  const photoCount = photo_urls?.length || 0;

  // Voice duration formatted
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Reaction names
  const reactionNames = Object.entries(reaction_counts || {})
    .filter(([, count]) => count > 0)
    .map(([type, count]) => {
      const emojis = { love: '❤️', laugh: '😂', wow: '😮', cry: '😢', thank_you: '🙏' };
      return `${emojis[type] || '👍'} ${count}`;
    })
    .join(' ');

  const handleClick = () => {
    onView?.(milestone);
    // Navigate to story view if linked, otherwise to milestone detail view
    if (life_event_id) {
      navigate(`/stories/${life_event_id}`);
    } else {
      navigate(`/my-life/milestones/${id}`);
    }
  };

  return (
    <div className="flex items-start gap-4 pl-4">
      {/* Timeline dot - large for milestones */}
      <div
        className="
          w-6 h-6 rounded-full flex items-center justify-center
          bg-fb-olive text-white text-xs flex-shrink-0
          mt-4
        "
      >
        {emoji || '📍'}
      </div>

      {/* Connector line */}
      <div className="w-8 h-px bg-gray-300 mt-7 flex-shrink-0" />

      {/* Card */}
      <div
        className="
          flex-1 bg-gray-50 rounded-xl p-4
          border border-gray-200
          hover:shadow-md cursor-pointer
          transition-all duration-200
        "
        onClick={handleClick}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-serif font-bold text-lg text-gray-900">
              {title}
            </h4>
            <p className="font-sans text-xs text-gray-500">
              {dateStr}
              {location && ` - ${location}`}
            </p>
          </div>
          <span className="text-fb-olive text-sm font-semibold">
            View →
          </span>
        </div>

        {/* Media indicators */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {photoCount > 0 && (
            <div className="flex items-center gap-1 px-3 py-1 rounded-lg bg-gray-100">
              <span>📷</span>
              <span className="font-sans text-xs text-gray-600">
                {photoCount} photo{photoCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {voice_url && (
            <div className="flex items-center gap-1 px-3 py-1 rounded-lg bg-green-50">
              <span>🎙</span>
              <span className="font-sans text-xs text-fb-olive">
                {formatDuration(voice_duration) || 'Voice'}
              </span>
            </div>
          )}

          {text && (
            <div className="flex items-center gap-1 px-3 py-1 rounded-lg bg-gray-100">
              <span>✍️</span>
              <span className="font-sans text-xs text-gray-600">Story</span>
            </div>
          )}
        </div>

        {/* Reactions */}
        {reactionNames && (
          <p className="font-sans text-xs text-gray-500">
            {reactionNames}
          </p>
        )}

        {/* Questions badge */}
        {questions && questions.length > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <p className="font-sans text-xs text-fb-terra">
              💬 {questions.length} question{questions.length !== 1 ? 's' : ''} from family
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

TimelineMilestoneCard.propTypes = {
  milestone: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    year: PropTypes.number.isRequired,
    month: PropTypes.number,
    location: PropTypes.string,
    emoji: PropTypes.string,
    voice_url: PropTypes.string,
    voice_duration: PropTypes.number,
    text: PropTypes.string,
    photo_urls: PropTypes.array,
    reaction_counts: PropTypes.object,
    questions: PropTypes.array,
    life_event_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onView: PropTypes.func,
};

TimelineMilestoneCard.defaultProps = {
  onView: null,
};

export default TimelineMilestoneCard;
